import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { InputBase } from '@mui/material';
import { Search as SearchIcon, Close as ClearIcon } from '@mui/icons-material';
import { Tooltip } from '../tooltip';
import { getTextValidCharacters } from '../../../utils/common-utils';
import './styles.scss';
import { Button } from '../button';

interface InputSearchProps {
  value?: string;
  placeholder?: string;
  isLoading?: boolean;
  helpText?: string;
  onSearch?: (text: string) => void;
}

const InputSearch = ({ value, placeholder, isLoading = true, helpText = '', onSearch = () => {} }: InputSearchProps) => {
  const [text, setText] = useState<string>('');
  const [lastSearch, setLastSearch] = useState<string>();
  const [isOpenHelp, setIsOpenHelp] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (value || (value === '' && value !== text)) {
      const finalValue = getTextValidCharacters(value);
      setText(finalValue);
      setLastSearch(finalValue);
    }
  }, [value]);

  const onSearchHandler = () => {
    if (isLoading || text === lastSearch) return;
    onSearch(text);
    setLastSearch(text);
    setIsOpenHelp(false);
  };

  const onClearHandler = () => {
    if (isLoading) return;
    setLastSearch(undefined);
    setText('');
    onSearch('');
  };

  const onKeyDownHandler = (e: any) => {
    if (e.key === 'Enter') {
      onSearchHandler();
    }
  };

  const onTextChangeHandler = (e: ChangeEvent<HTMLInputElement>) => setText(getTextValidCharacters(e.currentTarget.value));

  const ButtonSearch = () => (
    <Button variant="contained" onClick={onSearchHandler} disableElevation>
      <SearchIcon />
    </Button>
  );
  const ButtonClear = () => (
    <Button variant="contained" onClick={onClearHandler} disableElevation>
      <ClearIcon />
    </Button>
  );
  const ButtonAction = () => (text.length > 0 && text === lastSearch ? <ButtonClear /> : <ButtonSearch />);

  return (
    <Tooltip
      PopperProps={{
        disablePortal: true,
      }}
      classes={{
        tooltip: 'input_search__tooltip',
        arrow: 'input_search__tooltip__arrow',
      }}
      open={isOpenHelp}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={helpText}
    >
      <div ref={searchRef} className="input_search">
        <InputBase
          placeholder={placeholder}
          value={text}
          onChange={onTextChangeHandler}
          onKeyDown={onKeyDownHandler}
          onFocus={() => setIsOpenHelp(text.length === 0)}
          onBlur={() => setTimeout(() => setIsOpenHelp(false), 100)}
          className="input_search__input"
        />
        <ButtonAction />
      </div>
    </Tooltip>
  );
};

export default InputSearch;
